<template>
  <find-patient-modal :open="isFindPatientModalOpen" @close="toggleFindPatientModal">
  </find-patient-modal>
  <NavBar @close-all="closeAll" @open-find-patient-modal="toggleFindPatientModal"></NavBar>
  <UserProfile v-if="showUserProfile" @home-clicked="handleCancelFromUserProfile" />
  <div v-else>
    <div class="side-drawer">
      <button class="drawer-button" title="Home" @click="openConfirmationModal('isHomePressed', closeAll)"
        :class="{ 'icon-selected': !isAgendaOpen && !isTBSchedulerOpen && !isTBPhysicianScheduleOpen && !isPatientScheduleOpen }">
        <span class="material-symbols-outlined icon-button-to-primary">home</span>
        <span>Home</span>
      </button>
      <button class="tb-drawer-button" title="Upcoming Meetings" @click="openConfirmationModal('isAgendaOpen')"
        :class="{ 'icon-selected': isAgendaOpen }">
        <span class="material-symbols-outlined icon-button-to-primary">event_upcoming</span>
        <span>Upcoming</span>
      </button>
      <button v-if="isUserTBPersonnel" class="tb-drawer-button" title="Patient Scheduler"
        @click="openConfirmationModal('isPatientScheduleOpen')" :class="{ 'icon-selected': isPatientScheduleOpen }">
        <span class="material-symbols-outlined icon-button-to-primary">calendar_add_on</span>
        <span>Patient Scheduler</span>
      </button>
      <button v-if="isUserTBPersonnel" class="tb-drawer-button" title="Invite Physician"
        @click="openConfirmationModal('isTBPhysicianScheduleOpen')"
        :class="{ 'icon-selected': isTBPhysicianScheduleOpen }">
        <span class="material-symbols-outlined icon-button-to-primary">outgoing_mail</span>
        <span>Invite Physician</span>
      </button>
      <button v-if="isUserTBPersonnel" class="tb-drawer-button" title="Tumor Board Settings"
        @click="openConfirmationModal('isTBSchedulerOpen')" :class="{ 'icon-selected': isTBSchedulerOpen }">
        <span class="material-symbols-outlined icon-button-to-primary">settings</span>
        <span>Settings</span>
      </button>
    </div>
    <BaseDrawer v-if="isAgendaOpen" :is-open="isMeetingClicked" :speed="200"
      @close="openConfirmationModal('isAgendaOpen')" maxWidth="75%">
      <div>
        <div class="back-btn" @click="goBackMeeting()"> <span class="material-symbols-outlined">arrow_back_ios</span>
          <span>Go Back</span>
        </div>
        <Agenda @close-agenda="openConfirmationModal('isAgendaOpen')" @form-change="handleChange"
          @reset-confirmation="resetConfirmationModal"
          @request-start-session="callback => openConfirmationModal('isAgendaOpen', callback)" />
      </div>
    </BaseDrawer>
    <BaseDrawer v-if="isAgendaOpen" :is-open="isAgendaOpen && !isMeetingClicked" :speed="200"
      @close="openConfirmationModal('isAgendaOpen')" maxWidth="40%">
      <div>
        <UpcomingMeeting @meeting-clicked="isMeetingClicked = true"></UpcomingMeeting>
      </div>
    </BaseDrawer>
    <div v-if="isTBSchedulerOpen" class="tb-scheduler-view">
      <div class="tb-scheduler-column">
        <AdminTumorBoard @form-change="handleChange" @reset-confirmation="resetConfirmationModal" />
      </div>
    </div>
    <div v-else-if="isTBPhysicianScheduleOpen" class="tb-scheduler-view">
      <div class="tb-scheduler-column">
        <TBPhysicianSchedule @form-change="handleChange" @reset-confirmation="resetConfirmationModal" />
      </div>
    </div>
    <div v-if="isPatientScheduleOpen" class="tb-scheduler-view">
      <div class="tb-scheduler-column">
        <PatientSchedule @open-find-patient-modal="toggleFindPatientModal" @form-change="handleChange"
          @reset-confirmation="resetConfirmationModal" />
      </div>
    </div>
    <div v-else class="desktop-view">
      <div class="info-column">
        <div class="card-container patient-card">
          <PatientCard></PatientCard>
        </div>
        <div class="card-container tb-card">
          <TumorBoardCard></TumorBoardCard>
        </div>
      </div>
      <div class="timeline-column">
        <div class="card-container">
          <div class="center-item">
            <ClinicalTimelineCard v-if="patientId"></ClinicalTimelineCard>
            <LandingCenterCard v-else @open-find-patient-modal="toggleFindPatientModal"></LandingCenterCard>
            <transition name="fade">
              <div class="center-modal" v-if="isCenterColumnOpen">
                <transition name="fade">
                  <ReportViewerCard v-if="areReportsOpen" @close="closeCenterColumn()"
                    @refresh-diagnostics-tab="onRefreshDiagnosticsTab" @close-upload-form="closeUploadForm"
                    @open-upload-form="openUploadForm" ref="reportViewerCard" />
                </transition>
                <transition name="fade">
                  <GenomicsTestResults @close="closeCenterColumn()" v-if="isGenomicsOpen"></GenomicsTestResults>
                </transition>
                <transition name="fade">
                  <PathologyViewer v-if="isPathologyOpen"></PathologyViewer>
                </transition>
              </div>
            </transition>
          </div>
        </div>
      </div>
      <Transition name="fade">
        <ReportViewerForm v-show="showUploadForm" :card-name="fileToEdit?.uuid ? 'Edit File' : 'Upload Form'"
          :show-upload="true" :item-to-edit="fileToEdit" @update-list="updateFileList()"
          @refresh-diagnostics-tab="onRefreshDiagnosticsTab" @close-upload-form="showUploadForm = false">
        </ReportViewerForm>
      </Transition>
      <div class="diagnostics-column card-elevated">
        <div class="diag-container">
          <DiagnosticsCard ref="diagnosticsCardEl"></DiagnosticsCard>
        </div>
      </div>
    </div>
    <ConfirmationModal v-if="showConfirmationModal" @close-confirmation="showConfirmationModal = false"
      @close-drawer="handleDoNotSave" :confirmation-callback="confirmationCallback" />
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
// Copyright (c) 2023 datma, inc.
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import NavBar from '@/ux3/components/NavBar.vue'
import BaseDrawer from '@/ux3/components/BaseDrawer/BaseDrawer.vue'
import PatientCard from '@/ux3/cards/PatientCard/PatientCard.vue'
import ClinicalTimelineCard from '@/ux3/cards/ClinicalTimelineCard/ClinicalTimelineCard.vue'
import DiagnosticsCard from '@/ux3/cards/DiagnosticsCard/DiagnosticsCard.vue'
import TumorBoardCard from '@/ux3/cards/TumorBoardCard/TumorBoardCard.vue'
import UpcomingMeeting from '@/ux3/cards/TumorBoardSideBar/UpcomingMeeting.vue'
import Agenda from '@/ux3/cards/TumorBoardSideBar/Agenda.vue'
import ReportViewerCard from '../../ux3/cards/ReportViewer/ReportViewerCard.vue'
import ReportViewerForm from '../../ux3/cards/ReportViewer/ReportViewerForm.vue'
import GenomicsTestResults from '@/ux3/components/Genomics/Results/GenomicsTestResults.vue'
import { closeCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'
import PathologyViewer from '@/ux3/components/Pathology/Results/PathologyViewer.vue'
import AdminTumorBoard from '@/ux3/cards/ManageTumorBoard/AdminTumorBoard.vue'
import UserProfile from '@/ux3/components/UserProfile/UserProfile.vue'
import PatientSchedule from '@/ux3/cards/PatientSchedule/PatientSchedule.vue'
import LandingCenterCard from '@/ux3/cards/LandingCenterCard/LandingCenterCard.vue'
import TBPhysicianSchedule from '@/ux3/components/TBPhysicianSchedule/TBPhysicianSchedule.vue'
import ConfirmationModal from '@/ux3/components/ConfirmationModal/ConfirmationModal.vue'
import FindPatientModal from '@/ux3/components/FindPatientModal/FindPatientModal.vue'
import { getScheduledPatients } from '@/ux3/cards/TumorBoardSideBar/getScheduledPatients.js'
import { getUniqueUpcomingValues, groupDataByObjectProperty } from '@/common/shared.js'
import { showToast } from '@/utils/utils'

const store = useStore()
const route = useRoute()

const patientId = computed(() => store.getters.session?.patient?.id || '')
const isCenterColumnOpen = computed(() => !!store.getters.session?.centerColumnState?.container)
const isGenomicsOpen = computed(() => !!store.getters.session?.centerColumnState?.genomics)
const isPathologyOpen = computed(() => !!store.getters.session?.centerColumnState?.pathology)
const areReportsOpen = computed(() => !!store.getters.session?.centerColumnState?.reports)
const showUserProfile = computed(() => store.getters.session?.showUserProfile)
const userRoles = computed(() => store.getters.userRoles)

const diagnosticsCardEl = ref(null)
const changeDetected = ref(false)
const showConfirmationModal = ref(false)
const newDrawer = ref('')
const isMeetingClicked = ref(false)
const isTBSchedulerOpen = ref(false)
const isTBPhysicianScheduleOpen = ref(false)
const isPatientScheduleOpen = ref(false)
const isUserTBPersonnel = ref(false)
const isAgendaOpen = ref(false)
const confirmationCallback = ref(() => { })
const isFindPatientModalOpen = ref(false)
const isHomePressed = ref(false)
const showUploadForm = ref(false)
const fileToEdit = ref({})
const reportViewerCard = ref(null)

// when we select a new patient, close the Genomics card
watch(patientId, () => closeCenterColumn())

// File has been uploaded we need to refresh the corresponding tab
const onRefreshDiagnosticsTab = (tabName) => {
  if (diagnosticsCardEl.value) {
    diagnosticsCardEl.value.refreshATab(tabName)
  }
}

const toggleFindPatientModal = () => isFindPatientModalOpen.value = !isFindPatientModalOpen.value

const updateFileList = () => { reportViewerCard.value.updateList() }

const openUploadForm = (file) => {
  fileToEdit.value = file
  showUploadForm.value = true
}

const closeUploadForm = () => {
  fileToEdit.value = {}
  showUploadForm.value = false
}

// Used to close all modals/drawers
const closeAll = () => {
  closeCenterColumn()
  isAgendaOpen.value = false
  isTBSchedulerOpen.value = false
  isTBPhysicianScheduleOpen.value = false
  isPatientScheduleOpen.value = false
  store.dispatch('setShowUserProfile', { showUserProfile: false, publish: true })
}

const handleCancelFromUserProfile = () => {
  store.dispatch('setShowUserProfile', { showUserProfile: false, publish: true })
}

// Confirmation modal functions
const drawerStates = {
  'isPatientScheduleOpen': isPatientScheduleOpen,
  'isTBPhysicianScheduleOpen': isTBPhysicianScheduleOpen,
  'isTBSchedulerOpen': isTBSchedulerOpen,
  'isAgendaOpen': isAgendaOpen,
  'isHomePressed': isHomePressed,
}

const toggleDrawer = (drawerStateRef) => {
  const isDrawerOpen = drawerStates[drawerStateRef]
  if (isDrawerOpen) { isDrawerOpen.value = !isDrawerOpen.value }

  Object.keys(drawerStates).forEach(key => {
    if (key !== drawerStateRef) { drawerStates[key].value = false }
  })

  if (drawerStateRef === 'isAgendaOpen') {
    if (isMeetingClicked.value) {
      isMeetingClicked.value = false
    }
  }

  if (drawerStateRef === 'isHomePressed') {
    isHomePressed.value = false
  }
}

const handleChange = () => changeDetected.value = true

const resetConfirmationModal = () => {
  showConfirmationModal.value = false
  newDrawer.value = ''
  changeDetected.value = false
}

const isDrawerAlreadyOpen = () => Object.values(drawerStates).some(drawer => drawer.value)

const openConfirmationModal = (incomingDrawerName, optionalCallback) => {
  confirmationCallback.value = optionalCallback

  if (incomingDrawerName === 'isHomePressed') {
    isHomePressed.value = true
  }

  if (isDrawerAlreadyOpen() && changeDetected.value) {
    newDrawer.value = incomingDrawerName
    showConfirmationModal.value = true
  } else {
    toggleDrawer(incomingDrawerName)
  }
}

const handleDoNotSave = () => {
  toggleDrawer(newDrawer.value)
  confirmationCallback.value = () => { }
  showConfirmationModal.value = false
  changeDetected.value = false
  newDrawer.value = ''
}
// End confirmation modal functions

const goBackMeeting = () => isMeetingClicked.value = false

async function handleAgendaRouteQuery(route, store, isMeetingClicked, isAgendaOpen) {
  if (!route.query.calendar_id) { return }

  const scheduledPatientFromResponse = await getScheduledPatients()
  store.dispatch('setScheduledPatients', { scheduledPatientFromResponse, publish: true })

  const uniqueMeetings = getUniqueMeetings(scheduledPatientFromResponse)
  const matchedMeeting = findMeetingById(uniqueMeetings, route.query.calendar_id)

  if (matchedMeeting) {
    const meeting = createMeetingObject(matchedMeeting)
    // A nested array structure is used when setting the upcoming meetings in UpcomingMeeting.vue
    // TODO: Investigate why we are using this and refactor to avoid nested arrays if it's unnecessary.
    const upcomingMeetings = [[meeting]]
    store.dispatch('setSessionMeeting', { meeting, publish: true })
    store.dispatch('setUpcomingMeetings', { upcomingMeetings, publish: true })
    isMeetingClicked.value = true
    isAgendaOpen.value = true
  } else {
    console.log('why')
    showToast('error', `Error: Could not find tumorboard meeting with id: ${route.query.calendar_id}`)
  }

  // useRouter.replace causes a re-render. This will remove the query params without re-rendering.
  window.history.replaceState({}, document.title, window.location.pathname)
}

const findMeetingById = (meetings, scheduleId) => meetings.find(meeting => meeting.calendar_id === scheduleId)

const getUniqueMeetings = (scheduledPatients) => {
  const groupedMeetings = groupDataByObjectProperty(scheduledPatients)
  return getUniqueUpcomingValues(groupedMeetings)
}

// The extra properties in this object are mimicking what is stored when a
// PrimeVue row is clicked and saved to the store (how this currently works in UpcomingMeeting)
const createMeetingObject = (foundMeeting) => ({
  data: foundMeeting,
  index: 0,
  originalEvent: PointerEvent,
  type: 'row',
})

onMounted(async () => {
  const body = document.body
  document.documentElement.style.overflowY = 'hidden'
  body.style.overflowY = 'hidden'
  body.style.fontFamily = 'Roboto'
  const app = document.getElementById('app')
  app.style.position = 'static'  // fix body margin problem
  closeCenterColumn()
  isUserTBPersonnel.value = userRoles.value.includes('tb-personnel')

  handleAgendaRouteQuery(route, store, isMeetingClicked, isAgendaOpen)
})
</script>

<style lang="scss" scoped>
.desktop-view {
  display: flex;
  margin-left: 3rem;
  width: calc(100vw - 3rem);
  height: calc(100vh - 70px);
}

.tb-scheduler-view {
  display: flex;
  margin-left: 4rem;
  margin-top: 1rem;
  width: calc(100% - 88px);
  height: calc(100% - 98px);
}

.tb-scheduler-column {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 98px);
}

.tb-container {
  height: fit-content;
  max-height: 300px;
  width: 100%;
}

.info-column {
  width: 20%;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 98px);
}

.timeline-column {
  width: 55%;
  height: calc(100vh - 126px);
}

.old-tumor-board-column {
  width: 100%;
  height: calc(100vh - 70px);
}

.timeline-column .base-card {
  height: calc(100vh - 126px);
}

.diagnostics-column {
  container-type: inline-size;
  width: 25%;
  margin: 1rem 0 1rem 1rem;
  height: calc(100vh - 126px);
  background-color: var(--Grey100);
  border-radius: 6px;
  overflow: hidden;
}

.test-results {
  height: calc(100vh - 70px);
}

.card-container,
.diagnostics-column {
  margin: 28px;
  position: relative;
}

.timeline-column .card-container {
  margin: 28px 0;
  position: relative;
}

.center-modal {
  position: absolute;
  background-color: var(--White);
  width: 100%;
  height: calc(100vh - 126px);
  z-index: 70;
  top: 0;
  left: 0;
  border-radius: 16px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.side-drawer {
  background-color: var(--White);
  border-style: none;
  box-shadow: 0 0 3px #b9b9b9;
  width: 3.3rem;
  cursor: pointer;
  font: inherit;
  position: absolute;
  top: 71px;
  left: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  &>span {
    display: block;
    margin: 0 auto;
    padding-top: 3rem;
  }
}

.drawer-button {
  outline: none;
  border: none;
  background-color: transparent;
  margin-top: 1rem;
  transition: all linear 200ms;
  cursor: pointer;
  color: var(--Grey600);
  z-index: 91;
  font-size: .6rem;

  &:hover {
    color: var(--Primary);
  }
}

.tb-drawer-button {
  outline: none;
  border: none;
  background-color: transparent;
  margin-top: 0.5rem;
  transition: all linear 200ms;
  cursor: pointer;
  color: var(--Grey600);
  z-index: 91;
  font-size: .6rem;

  &:hover {
    color: var(--Primary);
  }
}

.agenda-drawer-button {
  border-radius: 0 16px 16px 0;
  border-style: none;
  box-shadow: 0 0 3px #b9b9b9;
  width: 3rem;
  cursor: pointer;
  font: inherit;
  position: absolute;
  top: 0;
  left: 4rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &>span {
    display: block;
    margin: 0 auto;
    padding-top: 3rem;

    &>svg {
      width: 1.2rem;
    }
  }
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 13px 0 0 22px;
  color: var(--Grey600);
  width: 100px;
  cursor: pointer;
  transition: all ease-in-out 300ms !important;
  font-size: 0.9rem;

  .material-symbols-outlined {
    font-size: 0.9rem;
    font-variation-settings: "FILL" 1, "wght" 900, "GRAD" 200, "opsz" 48;
  }

  &:hover,
  &:hover span {
    font-weight: 700;
    color: var(--Primary) !important;
    transition: all ease-in-out 300ms !important;
  }
}
</style>

<style type="scss">
.tb-card>div,
.info-column .tb-card,
.info-column .tb-card div .base-card {
  height: 100%;
  min-height: 0;
}

.timeline-column .base-card {
  height: calc(100vh - 126px);
}

.old-tumor-board-column .base-card {
  height: calc(96vh - 35px);
}

.tb-scheduler-column .base-card {
  height: calc(100vh - 126px);
  overflow: auto;
}

h3 {
  margin-left: 1rem;
}

.icon-selected {

  span,
  .material-symbols-outlined {
    color: var(--Primary);
  }

  .material-symbols-outlined {
    font-variation-settings: "FILL" 1, "wght" 900, "GRAD" 200, "opsz" 48;
  }
}

.tb-card {
  flex-grow: 2;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
</style>
